import React, { useEffect, useMemo, useRef, useState } from "react";

const clamp = (value, min, max) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const initialPosition = { x: 40, y: 140 };
const initialSize = { width: 340, height: 320 };
const minSize = { width: 260, height: 220 };

const QuestionNotepad = ({
  isOpen,
  onClose,
  questionId,
  questionLabel,
  notes = [],
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
}) => {
  const containerRef = useRef(null);
  const dragDataRef = useRef({ offsetX: 0, offsetY: 0 });
  const resizeDataRef = useRef({ startX: 0, startY: 0, startWidth: 0, startHeight: 0 });

  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setIsCollapsed(false);
  }, [isOpen, questionId]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event) => {
      if (typeof window === "undefined") return;
      const { offsetX, offsetY } = dragDataRef.current;
      const nextX = event.clientX - offsetX;
      const nextY = event.clientY - offsetY;
      const maxX = Math.max(0, window.innerWidth - size.width - 12);
      const visibleHeight = isCollapsed ? 72 : size.height;
      const maxY = Math.max(0, window.innerHeight - visibleHeight - 12);

      setPosition({
        x: clamp(nextX, 12 - size.width, maxX),
        y: clamp(nextY, 12, maxY),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, size.width, size.height, isCollapsed]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event) => {
      if (typeof window === "undefined") return;
      const { startX, startY, startWidth, startHeight } = resizeDataRef.current;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const nextWidth = Math.max(minSize.width, startWidth + deltaX);
      const nextHeight = Math.max(minSize.height, startHeight + deltaY);

      const maxWidth = window.innerWidth - position.x - 24;
      const maxHeight = window.innerHeight - position.y - 24;

      setSize({
        width: clamp(nextWidth, minSize.width, maxWidth),
        height: clamp(nextHeight, minSize.height, maxHeight),
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, position.x, position.y]);

  const handleDragStart = (event) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    dragDataRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setIsDragging(true);
  };

  const handleResizeStart = (event) => {
    event.stopPropagation();
    resizeDataRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startWidth: size.width,
      startHeight: size.height,
    };
    setIsResizing(true);
  };

  const headerActions = useMemo(
    () => ({
      toggleCollapse: () => setIsCollapsed((prev) => !prev),
      addNote: () => {
        if (questionId) {
          onCreateNote(questionId);
        }
      },
      close: onClose,
    }),
    [onClose, onCreateNote, questionId]
  );

  if (!isOpen) {
    return null;
  }

  const containerStyle = {
    top: `${position.y}px`,
    left: `${position.x}px`,
    width: `${size.width}px`,
    height: isCollapsed ? "auto" : `${size.height}px`,
  };

  const headerTitle = questionLabel ? `Notes ${questionLabel}` : "Notes";

  return (
    <>
      <div
        ref={containerRef}
        className="question-notepad"
        style={containerStyle}
      >
        <div className="question-notepad__header" onMouseDown={handleDragStart}>
          <span className="question-notepad__title">{headerTitle}</span>
          <div className="question-notepad__controls">
            <button
              type="button"
              onClick={headerActions.addNote}
              title="Add note"
              disabled={!questionId}
            >
              +
            </button>
            <button
              type="button"
              onClick={headerActions.toggleCollapse}
              title={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed ? "▢" : "–"}
            </button>
            <button type="button" onClick={headerActions.close} title="Close">
              ×
            </button>
          </div>
        </div>

        {!isCollapsed && (
          <div className="question-notepad__body">
            {notes.length === 0 && (
              <div className="question-notepad__empty">
                No notes yet. Add one to get started.
              </div>
            )}
            {notes.map((note) => (
              <div className="question-notepad__note" key={note.id}>
                <textarea
                  value={note.text}
                  onChange={(event) =>
                    onUpdateNote(questionId, note.id, event.target.value)
                  }
                  placeholder="Write your note here"
                />
                <div className="question-notepad__noteActions">
                  <span className="question-notepad__timestamp">
                    Last update: {
                      note.updatedAt
                        ? new Date(note.updatedAt).toLocaleTimeString()
                        : "—"
                    }
                  </span>
                  <button
                    type="button"
                    onClick={() => onDeleteNote(questionId, note.id)}
                    className="question-notepad__delete"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isCollapsed && (
          <div className="question-notepad__resize" onMouseDown={handleResizeStart} />
        )}
      </div>

      <style jsx>{`
        .question-notepad {
          position: fixed;
          z-index: 1200;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 20px 45px rgba(15, 34, 58, 0.18);
          border: 1px solid rgba(0, 0, 0, 0.08);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .question-notepad__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(90deg, #2b7fff, #5bbcff);
          color: #ffffff;
          padding: 12px;
          cursor: grab;
          user-select: none;
        }

        .question-notepad__header:active {
          cursor: grabbing;
        }

        .question-notepad__title {
          font-weight: 600;
          font-size: 0.95rem;
        }

        .question-notepad__controls {
          display: flex;
          gap: 8px;
        }

        .question-notepad__controls button {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: #ffffff;
          font-size: 0.95rem;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
          cursor: pointer;
        }

        .question-notepad__controls button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .question-notepad__controls button:hover {
          background: rgba(255, 255, 255, 0.35);
        }

        .question-notepad__body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 12px;
          overflow-y: auto;
        }

        .question-notepad__empty {
          color: #6c757d;
          font-size: 0.9rem;
          background: #f3f8ff;
          border: 1px dashed #c9ddff;
          border-radius: 10px;
          padding: 14px;
          text-align: center;
        }

        .question-notepad__note {
          display: flex;
          flex-direction: column;
          gap: 8px;
          background: #f9fbff;
          border-radius: 10px;
          border: 1px solid rgba(43, 127, 255, 0.15);
          padding: 10px;
        }

        .question-notepad__note textarea {
          width: 100%;
          min-height: 120px;
          resize: none;
          border: 1px solid rgba(43, 127, 255, 0.35);
          border-radius: 8px;
          padding: 8px;
          font-size: 0.95rem;
          font-family: inherit;
          outline: none;
        }

        .question-notepad__note textarea:focus {
          border-color: #2b7fff;
          box-shadow: 0 0 0 2px rgba(43, 127, 255, 0.2);
        }

        .question-notepad__noteActions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .question-notepad__timestamp {
          font-size: 0.75rem;
          color: #6c757d;
        }

        .question-notepad__delete {
          border: none;
          background: #ff4d4f;
          color: #fff;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .question-notepad__delete:hover {
          background: #d9363e;
        }

        .question-notepad__resize {
          position: absolute;
          width: 12px;
          height: 12px;
          right: 4px;
          bottom: 4px;
          cursor: se-resize;
          border-right: 2px solid rgba(43, 127, 255, 0.5);
          border-bottom: 2px solid rgba(43, 127, 255, 0.5);
        }

        @media (max-width: 768px) {
          .question-notepad {
            width: 92vw !important;
            left: 4vw !important;
            right: auto;
          }
        }
      `}</style>
    </>
  );
};

export default QuestionNotepad;
